// Today's jobs for each mastermind moved into the hub (docs/accelerator-ux-build-plan.md P1, Adam's yes of 20 Oct:
// the critique's admin 1 and 3), worked out when Today is read from what Masterminds reads itself
// (src/admin/mastermind/data.js: hub.mastermind_members, the groups and the sessions), for owner and admin:
//
// - REVIEW: 1 per group per session, while anyone in the group has that session's review due
//   (summary.flags.review_due). It goes once nobody in the group is due.
// - SET UP: 1 per member who's active, overdue or suspended, with only their first gap: no group, no month 1
//   actions, then a goal still to agree once its days are judged (members copied from the old site, whose goal
//   is "to_do", don't count).
// - The register: for the mastermind's sessions that ended in the last 7 days, how many of the group's current
//   members still have no mark (summary.flags.register_missing). The database's own register job for that
//   session takes the count while it's open, and stays open with it after the first mark closes it ("Register
//   taken"), until everyone's marked; a session with no database job left gets a row of its own, with no tick box.
//
// Nothing is stored, there's no new flag kind and nothing runs on a timer: each job is here only while what
// raised it is still true. Pure: it imports only the date helpers, so tests/e2e/admin-today.spec.js checks it
// on its own.
import { uk, calendarDay } from '../../lib/format.js'

const DAY_MS = 86400000
export const REGISTER_WINDOW_MS = 7 * DAY_MS
// the access statuses a set-up gap is chased for (a paused member's month waits, as the database's no_actions
// flag does)
const CURRENT = ['active', 'overdue', 'suspended']
// the resolution the database writes when a session's first register line closes its job (close_register_flag)
export const REGISTER_TAKEN = 'Register taken'

const endsOf = s => Date.parse(s.starts_at) + (s.duration_minutes || 0) * 60000
// First name order, then the full name (Masterminds' own order)
export const byName = (a, b) => (a.first_name || a.full_name || '').localeCompare(b.first_name || b.full_name || '', 'en-GB')
  || (a.full_name || '').localeCompare(b.full_name || '', 'en-GB')
  || String(a.person_id).localeCompare(String(b.person_id))

// The product's own fields every job carries, as hub.today_jobs gives them
const productFields = p => ({
  product_id: p.id, product_name: p.name, product_type: p.type, product_slug: p.slug, product_path: p.path || null,
})

// The goal's gap on a member, or null: { gap: 'goal', gone, days }, days gone since they joined (the goal is due
// on start day + days)
function goalGap(goal, nowMs) {
  if (!goal?.judged || !['not_yet', 'not_agreed'].includes(goal.state) || !goal.due_on || !goal.days) return null
  const left = calendarDay(goal.due_on).day - uk(nowMs).day
  return { gap: 'goal', gone: Math.max(0, goal.days - left), days: goal.days }
}

// A member's first set-up gap, or null
export function setupGap(m, nowMs) {
  if (!CURRENT.includes(m.access_status)) return null
  const s = m.summary || {}
  if (!m.cohort_id) return { gap: 'no_group' }
  if (!s.open_month && !s.last_month) return { gap: 'no_actions' }
  return goalGap(s.goal, nowMs)
}

// The jobs and the register counts for every mastermind read. reads: [{ product, members, groups, sessions }]
// (a read that failed is left out). Answers { jobs, registers }: jobs are REVIEW and SET UP, as Today's rows;
// registers maps a session id to { left, total, session, cohort, product } for the sessions that ended in the
// last 7 days. Nothing for support or a host.
export function mastermindJobs(reads, { nowMs = Date.now(), role = 'owner' } = {}) {
  const jobs = []
  const registers = new Map()
  if (role !== 'owner' && role !== 'admin') return { jobs, registers }
  for (const read of reads || []) {
    const { product } = read || {}
    if (!product) continue
    const members = read.members || []
    const groups = read.groups || []
    const sessions = read.sessions || []
    const byId = new Map(sessions.map(s => [s.id, s]))
    const groupOf = id => groups.find(g => g.id === id) || null
    const inGroup = id => members.filter(m => (m.cohort_id || null) === (id || null))

    // REVIEW: by group and session, once the session has ended
    const due = new Map()
    for (const m of members) {
      const r = m.summary?.flags?.review_due
      if (!r?.session_id) continue
      const s = byId.get(r.session_id) || { id: r.session_id, starts_at: r.starts_at, duration_minutes: 0 }
      if (!s.starts_at || endsOf(s) > nowMs) continue
      const key = `mm-review:${m.cohort_id || 'none'}:${r.session_id}`
      if (!due.has(key)) due.set(key, { key, cohortId: m.cohort_id || null, session: s, members: [] })
      due.get(key).members.push(m)
    }
    for (const d of due.values()) {
      const first = d.members.slice().sort(byName)[0]
      const group = groupOf(d.cohortId)
      jobs.push({
        job_key: d.key, kind: 'mm_review', kinds: ['mm_review'], status: 'open', flag_ids: [], raised_at: d.session.starts_at,
        ...productFields(product), cohort_id: d.cohortId, cohort_name: group?.name || null,
        session_id: d.session.id, session_starts_at: d.session.starts_at,
        mm: { left: d.members.length, total: inGroup(d.cohortId).length, first: { person_id: first.person_id, full_name: first.full_name, first_name: first.first_name } },
      })
    }

    // SET UP: each member's first gap
    for (const m of members) {
      const gap = setupGap(m, nowMs)
      if (!gap) continue
      jobs.push({
        job_key: `mm-setup:${m.person_id}`, kind: 'mm_setup', kinds: ['mm_setup'], status: 'open', flag_ids: [], raised_at: m.started_at,
        ...productFields(product), person_id: m.person_id, person_full_name: m.full_name, person_first_name: m.first_name,
        access_id: m.access_id, access_status: m.access_status, cohort_id: m.cohort_id, cohort_name: m.cohort_name, mm: gap,
      })
    }

    // the register: who in the group is still to mark, for each session that ended in the last 7 days
    for (const s of sessions) {
      if (s.status === 'cancelled' || !s.starts_at) continue
      const ends = endsOf(s)
      if (ends > nowMs || ends <= nowMs - REGISTER_WINDOW_MS) continue
      const pool = s.cohort_id ? inGroup(s.cohort_id) : members
      if (!pool.length) continue
      const left = pool.filter(m => (m.summary?.flags?.register_missing || []).some(r => r.session_id === s.id)).length
      registers.set(s.id, { left, total: pool.length, session: s, cohort: groupOf(s.cohort_id), product })
    }
  }
  return { jobs, registers }
}

// The database's jobs with the masterminds' in them: a register job for a counted session takes its count (and,
// closed at the first mark, opens again until everyone's marked); a counted session with someone still to mark
// and no database job gets a row of its own; REVIEW and SET UP go in beside them. mm: mastermindJobs' answer, or
// null while it's read (or when the reads failed), when the database's jobs are as they came.
export function withMastermindJobs(dbJobs, mm) {
  if (!mm) return dbJobs || []
  const seen = new Set()
  const out = (dbJobs || []).map(j => {
    if (j.kind !== 'register_missing' || !j.session_id) return j
    const r = mm.registers.get(j.session_id)
    if (!r) return j
    seen.add(j.session_id)
    if (!r.left) return j
    const count = { left: r.left, total: r.total }
    if (j.status === 'open') return { ...j, mm_register: count }
    // closed by the first mark: open again until everyone's marked. Ticked off by hand, it stays ticked.
    if (j.resolution === REGISTER_TAKEN) {
      return { ...j, mm_register: count, mm_reopened: true, status: 'open', done_at: null, done_by: null, done_by_name: null, resolution: null }
    }
    return j
  })
  for (const [sessionId, r] of mm.registers) {
    if (!r.left || seen.has(sessionId)) continue
    out.push({
      job_key: `mm-register:${r.cohort?.id || 'every'}:${sessionId}`, kind: 'register_missing', kinds: ['register_missing'], status: 'open',
      flag_ids: [], raised_at: new Date(endsOf(r.session)).toISOString(), ...productFields(r.product),
      session_id: sessionId, session_starts_at: r.session.starts_at, session_title: r.session.title || null,
      session_duration_minutes: r.session.duration_minutes ?? null, cohort_name: r.cohort?.name || null,
      mm_register: { left: r.left, total: r.total },
    })
  }
  return [...out, ...mm.jobs]
}

// A session's register count for Live today, or null when it isn't counted here
export const registerCount = (mm, sessionId) => {
  const r = mm?.registers?.get(sessionId)
  return r ? { left: r.left, total: r.total } : null
}
